import * as React from "react"
import { useMutation, useQuery } from "react-query"
import dayjs from "dayjs"
import { FiBellOff } from "react-icons/fi"
import { useToast, Flex, Tooltip, IconButton, Text, Box, Avatar } from "@chakra-ui/react"

import { AlertBikeEntity, AlertEntity, AlertType } from "../lib/types/alert"
import { OrgEntity } from "../lib/types/org"
import { OrgSettings } from "../lib/types/orgSettings"
import { useOrgs } from "./providers/OrgProvider"
import { fetcher } from "../lib/fetcher"
import { ALERTS } from "../lib/static/alert"

interface SilenceAlertRequest {
  type: string
}

interface AlertItemProps {
  alert: AlertEntity
  refetchAlerts: () => Promise<any>
  organization: OrgEntity
  onMuteAlert: () => void
}

export const AlertItem: React.FC<AlertItemProps> = (props) => {
  const toast = useToast()
  const { currentOrgs } = useOrgs()

  const { data: orgSettings } = useQuery<OrgSettings, string>(
    `organizations/${props.organization.uuid}/preference/`,
    { staleTime: Infinity },
  )
  const idleBicycleDuration = orgSettings?.idle_bicycle_duration || null

  const { mutateAsync: silenceAlert, isLoading } = useMutation<AlertEntity, unknown, SilenceAlertRequest>(
    (data) => fetcher(`alerts/${props.alert.uuid}/actions/`, { body: JSON.stringify(data), method: "post" }),
  )
  const handleSilenceAlert = async () => {
    try {
      props.onMuteAlert()
      const res = await silenceAlert({ type: "silence" })
      if (res?.state === "silenced") {
        // await props.refetchAlerts() // removed due to slow rendering
      } else if (res?.detail?.actions[0].code === "action_not_allowed") {
        // this case can happen when user double-clicks
        // await props.refetchAlerts() // await props.refetchAlerts()
      } else {
        toast({ description: "Error silencing alerts", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  return (
    <Flex align="center" justify="space-between" w="100%" p={1}>
      <Flex align="center">
        {currentOrgs.length > 1 && (
          <Tooltip label={props.organization.name}>
            <Avatar mr={2} name={props.organization.name} src={props.organization.icon} size="xs" />
          </Tooltip>
        )}
        <Box>
          <Text fontSize="0.8rem" fontWeight="bold" color="gray.700">
            {ALERTS[props.alert.alert_type as AlertType].label}
          </Text>
          <Text fontSize="0.7rem" color="gray.500">
            {props.alert.alert_type === "lock.bat.low"
              ? ALERTS["lock.bat.low"].description(
                  Math.ceil((props.alert.extra as AlertBikeEntity)?.state_of_charge || 0),
                )
              : idleBicycleDuration && props.alert.alert_type === "bicycle.too_long_idle"
              ? ALERTS["bicycle.too_long_idle"].description(idleBicycleDuration)
              : ALERTS[props.alert.alert_type as AlertType].description()}
          </Text>
        </Box>
      </Flex>
      <Flex align="flex-end" flexDir="column">
        <Tooltip label="Silence alert" zIndex={10}>
          <IconButton
            size="sm"
            boxSize="26px"
            minW="26px"
            variant="ghost"
            aria-label="Silence alert"
            icon={<FiBellOff />}
            isLoading={isLoading}
            onClick={handleSilenceAlert}
          />
        </Tooltip>
        <Text fontSize="0.5rem" color="gray.600" textAlign="right" whiteSpace="nowrap">
          {dayjs(props.alert.created).fromNow()}
        </Text>
      </Flex>
    </Flex>
  )
}
